import {useCallback} from 'react';

import {EncodeObject} from '@cosmjs/proto-signing';
import {
  DeliverTxResponse,
  SigningStargateClient,
  StdFee,
} from '@cosmjs/stargate';
import {sleep} from '@cosmjs/utils';
import {useQueryClient} from '@tanstack/react-query';

import {ELYS_CHAIN} from 'app/chains/chains.constants';
import {CosmosChainId} from 'app/chains/cosmos/cosmos.types';

import {useAuthentication} from '../useAuthentication';
import {signingStargateClientQuery} from '../cosmos/useSigningStargateClient';
import {useWeb3AuthModalStore} from './useWeb3AuthModalStore';
import {useTransactionNotifications} from '../useTransactionNotifications';

export type ExecuteOptions = {
  chainId?: CosmosChainId;
  gasUnits?: number | undefined; // recommended
  fee?: StdFee | 'auto' | number;
  sender?: string;
  onSuccess?: (response: string) => void;
  onSuccessNotification?: (message: DeliverTxResponse) => void;
  onError?: (error: unknown) => void;
  onErrorNotification?: (message: Error) => void;
};

// We use different chain id in case we want to transfer from a different chain
export const useExecuteChainModule = () => {
  const queryClient = useQueryClient();
  const {address, provider} = useAuthentication();

  const setIsOpen = useWeb3AuthModalStore(state => state.setIsOpen);
  const pendingExecution = useWeb3AuthModalStore(
    state => state.pendingExecution,
  );
  const setPendingExecution = useWeb3AuthModalStore(
    state => state.setPendingExecution,
  );

  const {
    notifyTransactionLoading,
    notifyTransactionError,
    notifyTransactionSuccess,
  } = useTransactionNotifications();

  const pollForTx = useCallback(
    async (
      client: SigningStargateClient,
      txId: string,
      options?: ExecuteOptions,
    ) => {
      const result = await client.getTx(txId);

      await sleep(3_000);

      if (!result) {
        pollForTx(client, txId);
      } else {
        const response = {
          code: result.code,
          height: result.height,
          txIndex: result.txIndex,
          events: result.events,
          rawLog: result.rawLog,
          transactionHash: txId,
          msgResponses: result.msgResponses,
          gasUsed: result.gasUsed,
          gasWanted: result.gasWanted,
        };

        if (response.code === 0) {
          options?.onSuccessNotification?.(response);
        } else {
          options?.onErrorNotification?.(new Error(response.rawLog));
        }
      }
    },
    [],
  );

  const executeTransaction = useCallback(
    async (
      msgs: EncodeObject[],
      options?: ExecuteOptions,
    ): Promise<string | null> => {
      const chainId = options?.chainId ?? ELYS_CHAIN.chainId;
      if (!address) return null;

      const randomId = Math.random().toString();
      try {
        notifyTransactionLoading({
          id: randomId,
          duration: Infinity,
        });

        const client = await queryClient.ensureQueryData(
          signingStargateClientQuery(chainId, provider),
        );
        const tx = await client.signAndBroadcastSync(
          options?.sender || address,
          msgs,
          // options?.gasUnits
          //   ? calculateFee(Math.round(options?.gasUnits * 4), gasPrice)
          //   :
          options?.fee || 'auto',
        );

        notifyTransactionLoading({
          id: randomId,
          duration: 0,
        });

        options?.onSuccess?.(tx);
        notifyTransactionSuccess(chainId, tx);
        pollForTx(client, tx, options);
        return tx;
      } catch (error) {
        if (!(error instanceof Error)) throw error;

        console.error(error);

        options?.onError?.(error);

        notifyTransactionError(error.message);
      } finally {
        notifyTransactionLoading({
          id: randomId,
          duration: 0,
        });
      }

      return null;
    },
    [
      address,
      notifyTransactionError,
      notifyTransactionLoading,
      notifyTransactionSuccess,
      pollForTx,
      provider,
      queryClient,
    ],
  );

  const execute = useCallback(
    async (
      msgs: EncodeObject[],
      options?: ExecuteOptions,
    ): Promise<string | null> => {
      if (provider.type === 'web3_auth') {
        setIsOpen(true);
        setPendingExecution({msgs, options});
        return null;
      }

      return executeTransaction(msgs, options);
    },
    [provider?.type, executeTransaction, setIsOpen, setPendingExecution],
  );

  const handleConfirmWeb3Auth = useCallback(async () => {
    if (pendingExecution) {
      setIsOpen(false);
      await executeTransaction(pendingExecution.msgs, pendingExecution.options);
      setPendingExecution(null);
    }
    setIsOpen(false);
  }, [pendingExecution, setIsOpen, executeTransaction, setPendingExecution]);

  return {
    execute,
    handleConfirmWeb3Auth,
  };
};
